import React, { useState, useContext, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Link,
  useTheme,
  useMediaQuery,
  keyframes,
} from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import PdfViewerDialog from "components/common/PdfViewerDialog";
import { stripSourceCitations } from "utils/clinicalSources";

import AddRounded from "@mui/icons-material/AddRounded";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ForumIcon from "@mui/icons-material/Forum";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SchoolRounded from "@mui/icons-material/SchoolRounded";

import CustomMessageInput from "components/chat_new/CustomMessageInput";
import ChatSuggestions from "./ChatSuggestions";
import { AuthContext } from "context/Auth";
import { useRateLimit } from "hooks/useRateLimit";
import { invokeLLMPrompt } from "services/llmService";
import { retrieveClinicalReferences } from "services/clinicalReferencesService";
import AiDisclosureNotice from "shared/components/aiDisclosure/AiDisclosureNotice";

const MAX_SOURCES = 4;

const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.4; }
  50% { transform: scale(1.05); opacity: 0.7; }
  100% { transform: scale(0.95); opacity: 0.4; }
`;

function mergeSourcesByUrl(embeddedDocs, clinicalRefs) {
  const byUrl = new Map();
  for (const doc of embeddedDocs || []) {
    if (!doc?.downloadURL) continue;
    byUrl.set(doc.downloadURL, {
      downloadURL: doc.downloadURL,
      fileName: doc.fileName || "Document",
      similarity: typeof doc.similarity === "number" ? doc.similarity : null,
    });
  }
  for (const ref of clinicalRefs || []) {
    if (!ref?.downloadURL || byUrl.has(ref.downloadURL)) continue;
    byUrl.set(ref.downloadURL, {
      downloadURL: ref.downloadURL,
      fileName: ref.fileName || "Document",
      similarity: null,
    });
  }
  return Array.from(byUrl.values())
    .sort((a, b) => (b.similarity ?? -1) - (a.similarity ?? -1))
    .slice(0, MAX_SOURCES);
}

function displayLabel(source) {
  return (
    (source.fileName || "Document")
      .replace(/\.(pdf|docx?|txt|md)$/i, "")
      .replace(/[_-]/g, " ")
      .trim() || "Document"
  );
}

function SourceLinks({ sources, onOpenPdf }) {
  if (!sources?.length) return null;
  return (
    <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.15)" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        Source documents
      </Typography>
      {sources.map((s) => (
        <Box
          key={s.downloadURL}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
        >
          <PictureAsPdf
            sx={{ fontSize: "0.9rem", color: "error.main", flexShrink: 0 }}
          />
          <Link
            component="button"
            variant="caption"
            onClick={() => onOpenPdf(s)}
            sx={{ textAlign: "left", wordBreak: "break-word" }}
          >
            {displayLabel(s)}
          </Link>
        </Box>
      ))}
    </Box>
  );
}

// Assistant configuration - centralized in one place
const getAssistantConfig = (assistantType: string) => {
  const configs = {
    brainflash: {
      pageTitle: "BrainFlash",
      pageDescription: "Rapid clinical facts",
      introIcon: <FlashOnIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Get instant dosing, contraindications, and critical clinical information in seconds. Built for time-sensitive decisions when every moment counts.",
    },
    "deep-dive": {
      pageTitle: "DeepDive",
      pageDescription: "Medical reference",
      introIcon: <MenuBookIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Explore detailed pathophysiology, mechanisms, and evidence-based medicine. Your comprehensive resource for thorough clinical understanding.",
    },
    "peer-review": {
      pageTitle: "PeerView Case Consultation",
      pageDescription: "Virtual consultation",
      introIcon: <ForumIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Collaborate on challenging cases with structured clinical decision support. Navigate through history, examination, diagnostics, and treatment planning.",
    },
    "basic-medical-library": {
      pageTitle: "Medical Library",
      pageDescription: "General medical questions",
      introIcon: <SchoolRounded sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Learn about your body, common health conditions, and medical concepts in clear, simple language.",
    },
    general: {
      pageTitle: "Medical SuperIntelligence",
      pageDescription: "AI-powered medical assistance",
      introIcon: <MedicalServicesIcon sx={{ fontSize: 64 }} color="primary" />,
      introDescription:
        "Get comprehensive medical guidance and support for your health questions.",
    },
  };

  return configs[assistantType] || configs["general"];
};

const ChatPage = ({
  assistantType = "general",
}) => {
  const assistantConfig = getAssistantConfig(assistantType);
  const { pageTitle, introIcon, introDescription } = assistantConfig;

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: null,
    title: null,
  });

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const chatBoxRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const rateLimit = useRateLimit(null, null);

  // Ref to prevent double-firing the initiateChatWith state
  const hasProcessedInitialMessage = useRef(false);

  const handleNewChat = () => setMessages([]);

  const handleOpenPdf = (source) => {
    if (source?.downloadURL) {
      setPdfViewer({ open: true, url: source.downloadURL, title: displayLabel(source) });
    }
  };

  const handleSendNewMessage = async (message) => {
    const trimmed = (typeof message === "string" ? message : inputValue).trim();
    if (!trimmed || isTyping) return;

    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: trimmed, sources: [] }]);
    setIsTyping(true);

    try {
      const [result, clinicalRefs] = await Promise.all([
        invokeLLMPrompt({
          promptId: assistantType,
          userMessage: trimmed,
          includeEmbeddedDocuments: true,
          embeddedDocumentsOptions: { chunkLimit: 8 },
        } as any),
        retrieveClinicalReferences(trimmed, { promptId: assistantType }).catch(() => []),
      ]);

      const embeddedDocs = result.embeddedDocuments?.documents ?? [];
      const sources = mergeSourcesByUrl(embeddedDocs, clinicalRefs);
      const content = stripSourceCitations(result.output || "(No response)");

      setMessages((prev) => [...prev, { role: "assistant", content, sources }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${err.message || "Request failed."}`,
          isError: true,
          sources: [],
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  // Auto-scroll
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Reset on assistant type change
  useEffect(() => {
    setMessages([]);
  }, [assistantType]);

  // Handle location state (initiateChatWith)
  useEffect(() => {
    const initiateMessage = location.state?.initiateChatWith;
    if (initiateMessage && !hasProcessedInitialMessage.current) {
      hasProcessedInitialMessage.current = true;
      handleSendNewMessage(initiateMessage);
      navigate(location.pathname, {
        replace: true,
        state: { ...location.state, initiateChatWith: null },
      });
    }
  }, [navigate, location.pathname, location.state]);

  return (
    <Box
      sx={{
        height: { xs: "calc(100dvh - 65px)", sm: "100dvh" },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1, flexShrink: 0 }}>
        <AiDisclosureNotice
          setting="remote"
          variant="banner"
          locale={
            typeof navigator !== "undefined" &&
            navigator.language &&
            !navigator.language.toLowerCase().startsWith("en")
              ? navigator.language
              : "en"
          }
        />
      </Box>
      {/* Top bar — new chat button */}
      {user && !isXs && (
        <Box
          sx={{
            ...theme.mixins.toolbar,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: 3,
            backgroundColor: "#ffffff",
            flexShrink: 0,
            zIndex: 10,
          }}
        >
          <Button
            onClick={handleNewChat}
            startIcon={<AddRounded />}
            variant="outlined"
            sx={{
              display: { xs: "none", sm: "none", md: "none", lg: "flex" },
              borderColor: "#d1d5db",
              color: "#374151",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" },
            }}
          >
            New Chat
          </Button>
          <Tooltip title="New Chat">
            <IconButton
              onClick={handleNewChat}
              sx={{
                display: { xs: "none", sm: "flex", md: "flex", lg: "none" },
                color: "#64748b",
              }}
              size="small"
            >
              <AddRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Mobile floating new-chat button */}
      {user && isXs && (
        <Box sx={{ position: "fixed", top: "12px", right: "20px", zIndex: 1150 }}>
          <Tooltip title="New Chat">
            <IconButton onClick={handleNewChat} sx={{ color: "#64748b" }} size="small">
              <AddRounded />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Centered content */}
      <Box
        sx={{
          margin: "0 auto",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "100%",
          maxWidth: "970px",
          px: 2,
          backgroundColor: "#ffffff",
          ...(messages.length === 0
            ? {
                justifyContent: "flex-start",
                alignItems: "center",
                paddingTop: { sm: "1rem", md: "20vh" },
              }
            : { justifyContent: "flex-start" }),
        }}
      >
        {/* Intro screen */}
        {messages.length === 0 && (introIcon || introDescription) && (
          <Box sx={{ textAlign: "center", py: 0, px: 2, maxWidth: "600px", width: "100%" }}>
            {introIcon && (
              <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                {introIcon}
              </Box>
            )}
            <Typography
              variant="h4"
              sx={{
                mb: { xs: 1.5, sm: 2 },
                fontWeight: 600,
                fontSize: { xs: "1.75rem", sm: "2.125rem" },
              }}
            >
              {pageTitle}
            </Typography>
            {introDescription && (
              <Typography
                variant="body1"
                sx={{ mb: { xs: 1, sm: 3 }, color: "text.secondary", lineHeight: 1.6 }}
              >
                {introDescription}
              </Typography>
            )}
          </Box>
        )}

        {/* Message list */}
        {messages.length > 0 && (
          <Box
            ref={chatBoxRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              mb: 1,
            }}
          >
            {messages.map((msg, i) => (
              <Box key={i} sx={{ display: "flex", margin: "5px 0", width: "100%" }}>
                <Box
                  sx={{
                    backgroundColor: msg.role === "user" ? "primary.main" : msg.isError ? "#ff7979" : "#eee",
                    color: msg.role === "user" ? "primary.contrastText" : "text.primary",
                    borderRadius: msg.role === "user" ? "8px 0px 8px 8px" : "0px 8px 8px 8px",
                    padding: "10px",
                    maxWidth: "80%",
                    width: "fit-content",
                    marginLeft: msg.role === "user" ? "auto" : "0",
                    marginRight: msg.role === "user" ? "0" : "auto",
                    boxShadow: 1,
                    "& p": { margin: 0 },
                    "& p + p": { marginTop: "16px" },
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  <Typography variant="body1" component="span" sx={{ color: "inherit", wordBreak: "break-word" }}>
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Typography>
                  {msg.role === "assistant" && msg.sources?.length > 0 && (
                    <SourceLinks sources={msg.sources} onOpenPdf={handleOpenPdf} />
                  )}
                </Box>
              </Box>
            ))}

            {isTyping && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, pl: 2 }}>
                <Box
                  sx={{
                    width: 12, height: 12, borderRadius: "50%",
                    backgroundColor: "primary.main",
                    animation: `${pulse} 1.2s ease-in-out infinite`,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Thinking…
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Input */}
        <Box
          sx={{
            flexShrink: 0,
            width: "100%",
            ...(messages.length === 0
              ? { position: "relative", maxWidth: "600px" }
              : { marginBottom: "6px" }),
          }}
        >
          <CustomMessageInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            handleSendRequest={handleSendNewMessage}
            userData={null}
            hasMessages={messages.length > 0}
          />
          {messages.length === 0 && !rateLimit.isRateLimited && (
            <ChatSuggestions assistantType={assistantType} onSuggestionClick={handleSendNewMessage} />
          )}
        </Box>
      </Box>

      {/* PDF Viewer */}
      <PdfViewerDialog
        open={pdfViewer.open}
        url={pdfViewer.url}
        title={pdfViewer.title}
        onClose={() => setPdfViewer({ open: false, url: null, title: null })}
      />
    </Box>
  );
};

export default ChatPage;
